if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // acOlaSia: arrows navigate the product image, including thumbnail controls.
        this.mediaOrder = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'), (item) => item.dataset.mediaId);
        this.addEventListener('click', (event) => {
          const button = event.target.closest('.slider-button');
          if (!button || !this.contains(button) || !['previous', 'next'].includes(button.name)) return;
          const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'))
            .sort((a, b) => this.mediaOrder.indexOf(a.dataset.mediaId) - this.mediaOrder.indexOf(b.dataset.mediaId));
          if (items.length < 2) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
          const next = (current + (button.name === 'next' ? 1 : -1) + items.length) % items.length;
          this.setActiveMedia(items[next].dataset.mediaId, false);
        }, true);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.colorEvents?.abort();
        this.colorEvents = new AbortController();
        this.productRoot = this.closest('product-info');
        if (!this.productRoot) return;
        const signal = this.colorEvents.signal;
        this.productRoot.addEventListener('change', (event) => {
          if (!event.target.closest('variant-selects')) return;
          const option = event.target.matches('select') ? event.target.selectedOptions[0] : event.target;
          const media = this.colorMedia(option);
          if (!media) return;
          this.warmColorImage(media);
          // Paint the existing image now; Shopify still updates price, stock and the cart variant.
          this.setActiveMedia(media.dataset.mediaId, false, true);
        }, {capture: true, signal});
        for (const type of ['pointerover', 'focusin']) {
          this.productRoot.addEventListener(type, (event) => {
            const label = event.target.closest('variant-selects label[for]');
            const option = label ? document.getElementById(label.htmlFor) : event.target;
            this.warmColorImage(this.colorMedia(option));
          }, {passive: true, signal});
        }
        queueMicrotask(() => {
          if (!this.isConnected) return;
          const selected = this.productRoot.querySelectorAll('variant-selects input:checked, variant-selects option:checked');
          const media = Array.from(selected, (option) => this.colorMedia(option)).find(Boolean);
          if (media) this.setActiveMedia(media.dataset.mediaId, false, true);
          if (navigator.connection?.saveData) return;
          const preload = () => {
            if (!this.isConnected) return;
            const media = new Set(Array.from(this.productRoot.querySelectorAll('[data-acolasia-media-id]'), option => this.colorMedia(option)).filter(Boolean));
            media.forEach(item => this.warmColorImage(item));
          };
          if ('requestIdleCallback' in window) this.colorIdle = requestIdleCallback(preload, {timeout: 1500});
          else this.colorTimer = setTimeout(preload, 300);
        });
      }

      disconnectedCallback() {
        this.colorEvents?.abort();
        if (this.colorIdle) cancelIdleCallback(this.colorIdle);
        clearTimeout(this.colorTimer);
      }

      colorMedia(option) {
        const id = option?.dataset?.acolasiaMediaId;
        if (!id) return null;
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).find(item => item.dataset.mediaId.endsWith('-' + id));
      }

      warmColorImage(media) {
        const image = media?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.decode?.().catch(() => {});
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, preserveViewport = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (preserveViewport || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener('load', announce, {once: true});
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
